import { useState } from "react";
import { parseScan } from "@iltocat/shared";
import { CameraView, hasCamera } from "./CameraView.js";
import { Dialog } from "./Dialog.js";
import { Icon } from "./Icon.js";
import { Notice } from "./Notice.js";

/**
 * A serial number read off a box with the phone's camera, checked before it
 * is used.
 *
 * A box carries several barcodes -- part number, UPC, serial -- and the camera
 * takes whichever it sees first, so nothing goes into the form on its own. The
 * read stops the camera and shows what came back in a box a person can fix
 * (drop a prefix, say), with a warning when it looks like the wrong barcode:
 * one of our own labels, a retail product code, or a serial already listed.
 * Only "Use this" puts it in.
 *
 * `repeat` is for Receive, where a line is a stack of boxes: each serial is
 * added and the camera starts again for the next, until Done.
 */

/** A UPC or EAN: only digits, at a retail barcode's length. A serial is rarely that. */
const looksLikeProductCode = (text: string): boolean => /^\d{8}$|^\d{12,13}$/.test(text);

export function ScanSerialDialog({
  onUse,
  onClose,
  taken = [],
  repeat = false,
}: {
  onUse: (serial: string) => void;
  onClose: () => void;
  /** Serials already listed, so scanning one twice says so. */
  taken?: string[];
  repeat?: boolean;
}) {
  const [camera, setCamera] = useState<"on" | "denied" | "unsupported">(hasCamera() ? "on" : "unsupported");
  const [read, setRead] = useState<string | null>(null);
  const [value, setValue] = useState("");
  const [added, setAdded] = useState<string[]>([]);
  // A new key remounts the camera, which is how "scan again" starts it afresh.
  const [attempt, setAttempt] = useState(0);

  const serial = value.trim();
  const listed = [...taken, ...added].some((one) => one.trim().toLowerCase() === serial.toLowerCase());
  const label = read !== null && parseScan(read)?.kind !== "text";
  const warning = label
    ? "That is a utucat label, not a serial number. Aim at the barcode marked S/N."
    : listed
      ? "That serial is already listed."
      : looksLikeProductCode(serial)
        ? "That looks like a product barcode (UPC or EAN), not a serial number. Check the box for the one marked S/N."
        : null;

  function again() {
    setRead(null);
    setValue("");
    setAttempt((count) => count + 1);
  }

  function use() {
    if (!serial || label) return;
    onUse(serial);
    if (!repeat) return onClose();
    setAdded((now) => [...now, serial]);
    again();
  }

  return (
    <Dialog title={repeat ? "Scan serial numbers" : "Scan the serial number"} onClose={onClose}>
      <div className="stack">
        {camera === "unsupported" ? (
          <Notice tone="warn">This browser cannot use the camera here. Type the serial instead.</Notice>
        ) : camera === "denied" ? (
          <Notice tone="error">
            The camera could not start. Check that this site may use it, or type the serial.
          </Notice>
        ) : read === null ? (
          <>
            <CameraView
              key={attempt}
              aim="barcode"
              onRead={(text) => {
                setRead(text);
                setValue(text.trim());
              }}
              onFail={() => setCamera("denied")}
            />
            <p className="muted small">
              Aim at the barcode marked S/N. Boxes carry several, so you check what it reads before it is
              used.
            </p>
          </>
        ) : (
          <form
            className="stack"
            onSubmit={(event) => {
              event.preventDefault();
              use();
            }}
          >
            <label className="field">
              <span className="field-label">Read from the barcode</span>
              <input
                className="input mono"
                value={value}
                autoComplete="off"
                spellCheck={false}
                onChange={(event) => setValue(event.target.value)}
              />
            </label>
            {warning && <Notice tone="warn">{warning}</Notice>}
            <div className="panel-foot">
              <button type="button" className="button button-quiet" onClick={again}>
                <Icon name="camera" size={18} />
                Scan again
              </button>
              <button type="submit" className="button" disabled={!serial || label}>
                {repeat ? "Add, scan the next" : "Use this"}
              </button>
            </div>
          </form>
        )}

        {repeat && added.length > 0 && (
          <div className="panel-foot">
            <p className="muted small">
              {added.length === 1 ? "1 serial added." : `${added.length} serials added.`}
            </p>
            <button type="button" className="button button-ghost" onClick={onClose}>
              Done
            </button>
          </div>
        )}
      </div>
    </Dialog>
  );
}
